package pl.zar.tikthrow.util

import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.Serializable
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.decodeFromString
import pl.zar.tikthrow.shared.domain.Platform
import pl.zar.tikthrow.ui.media.MediaFileRegistry
import pl.zar.tikthrow.viewmodel.ScheduledPost
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import org.w3c.files.File
import org.w3c.xhr.FormData

actual suspend fun publishScheduledPost(post: ScheduledPost): PlatformPublishResult {
    if (post.platforms.isEmpty()) {
        return PlatformPublishResult(false, "Nie wybrano platformy")
    }
    val token = currentAuthToken()
        ?: return PlatformPublishResult(false, "Zaloguj sie przed publikacja")

    val results = post.platforms.map { platform ->
        when (platform) {
            Platform.YOUTUBE -> publishScheduledYouTube(post, token)
            Platform.TIKTOK -> publishScheduledTikTok(post, token)
            Platform.FACEBOOK -> publishScheduledFacebook(post, token)
            Platform.INSTAGRAM -> publishScheduledInstagram(post, token)
            Platform.DISCORD -> PlatformPublishResult(false, "Discord: publikacja poza kolejką wymaga skonfigurowanego kanału — użyj kolejki albo planu")
            Platform.TWITTER -> PlatformPublishResult(false, "Twitter: publikacja nie jest jeszcze wspierana")
        }
    }

    val success = results.all { it.success }
    val message = results.joinToString(" | ") { it.message }
    return PlatformPublishResult(success, message, results.firstNotNullOfOrNull { it.serverId })
}

private suspend fun publishScheduledYouTube(post: ScheduledPost, token: String): PlatformPublishResult {
    val media = post.media.firstOrNull { it.isVideo } ?: post.media.firstOrNull()
        ?: return PlatformPublishResult(false, "YouTube wymaga pliku wideo")
    val file = MediaFileRegistry.file(media.uri) as? File
        ?: return PlatformPublishResult(false, "Plik jest niedostepny. Wybierz go ponownie.")
    if (!file.type.startsWith("video/")) {
        return PlatformPublishResult(false, "YouTube wymaga pliku wideo")
    }

    return runCatching {
        val form = FormData()
        val title = post.caption.lineSequence().firstOrNull { it.isNotBlank() }?.take(100) ?: "Tikthrow upload"
        form.append("title", title)
        form.append("description", post.caption)
        form.append("publicationType", post.youtubePublicationType())
        post.youtubeAccountId?.takeIf { it.isNotBlank() }?.let { form.append("accountId", it) }
        form.append("media", file, file.name)
        val headers = Headers()
        headers.append("Authorization", "Bearer $token")
        val response = window.fetch(
            "$apiBaseUrl/api/publish/youtube",
            RequestInit(method = "POST", headers = headers, body = form)
        ).await()
        val text = response.text().await()
        PlatformPublishResult(
            success = response.ok,
            message = if (response.ok) "YouTube opublikowany: $text" else "YouTube nie opublikowany: $text"
        )
    }.getOrElse { exception ->
        PlatformPublishResult(false, "Blad publikacji YouTube: ${exception.message}")
    }
}

private suspend fun publishScheduledTikTok(post: ScheduledPost, token: String): PlatformPublishResult {
    val media = post.media.firstOrNull { it.isVideo } ?: post.media.firstOrNull()
        ?: return PlatformPublishResult(false, "TikTok wymaga pliku wideo")
    val file = MediaFileRegistry.file(media.uri) as? File
        ?: return PlatformPublishResult(false, "Plik jest niedostepny. Wybierz go ponownie.")
    if (!file.type.startsWith("video/")) {
        return PlatformPublishResult(false, "TikTok wymaga pliku wideo")
    }

    return runCatching {
        val form = FormData()
        form.append("caption", post.caption)
        form.append("privacyLevel", "SELF_ONLY")
        post.tiktokAccountId?.takeIf { it.isNotBlank() }?.let { form.append("accountId", it) }
        form.append("media", file, file.name)
        val headers = Headers()
        headers.append("Authorization", "Bearer $token")
        val response = window.fetch(
            "$apiBaseUrl/api/publish/tiktok",
            RequestInit(method = "POST", headers = headers, body = form)
        ).await()
        val text = response.text().await()
        PlatformPublishResult(
            success = response.ok,
            message = if (response.ok) "TikTok opublikowany: $text" else "TikTok nie opublikowany: $text"
        )
    }.getOrElse { exception ->
        PlatformPublishResult(false, "Blad publikacji TikTok: ${exception.message}")
    }
}

private suspend fun publishScheduledFacebook(post: ScheduledPost, token: String): PlatformPublishResult {
    return runCatching {
        val media = post.media.firstOrNull()
        val file = media?.let { MediaFileRegistry.file(it.uri) as? File }
        val hasFile = file != null && file.size.toLong() > 0
        // Resolve the real MIME type instead of defaulting to image/jpeg — a video with a
        // missing mimeType would otherwise be sent (and rejected) as an image.
        val resolvedMime = media?.mimeType?.takeIf { it.isNotBlank() }
            ?: file?.let { it.type.takeIf { t -> t.isNotBlank() } }
            ?: "application/octet-stream"
        val contentType = when {
            hasFile && (resolvedMime.startsWith("video/") || (media!!.fileName ?: file!!.name).isVideoFileNameJs()) -> "VIDEO"
            hasFile -> "PHOTO"
            else -> "TEXT"
        }
        val form = FormData()
        form.append("message", post.caption)
        form.append("contentType", contentType)
        form.append("contentMime", if (contentType == "VIDEO") "video/mp4" else resolvedMime)
        post.facebookAccountId?.takeIf { it.isNotBlank() }?.let { form.append("accountId", it) }
        if (hasFile && file != null) {
            form.append("media", file, media!!.fileName ?: file.name)
        }
        val headers = Headers()
        headers.append("Authorization", "Bearer $token")
        val response = window.fetch(
            "$apiBaseUrl/api/publish/facebook",
            RequestInit(method = "POST", headers = headers, body = form)
        ).await()
        val text = response.text().await()
        PlatformPublishResult(
            success = response.ok,
            message = if (response.ok) "Facebook opublikowany: $text" else "Facebook nie opublikowany: $text"
        )
    }.getOrElse { exception ->
        PlatformPublishResult(false, "Blad publikacji Facebook: ${exception.message}")
    }
}

private suspend fun publishScheduledInstagram(post: ScheduledPost, token: String): PlatformPublishResult {
    val media = post.media.firstOrNull()
        ?: return PlatformPublishResult(false, "Instagram wymaga pliku obrazu albo wideo")
    val file = MediaFileRegistry.file(media.uri) as? File
        ?: return PlatformPublishResult(false, "Plik jest niedostepny. Wybierz go ponownie.")

    return runCatching {
        // Resolve the real MIME type from the file (e.g. .mov → video/quicktime) so the server
        // classifies it as a video (REELS) instead of defaulting to an image.
        val resolvedMime = media.mimeType?.takeIf { it.isNotBlank() }
            ?: file.type.takeIf { it.isNotBlank() }
            ?: "application/octet-stream"
        val form = FormData()
        form.append("caption", post.caption)
        form.append("contentMime", resolvedMime)
        post.instagramAccountId?.takeIf { it.isNotBlank() }?.let { form.append("accountId", it) }
        form.append("media", file, media.fileName ?: file.name)
        val headers = Headers()
        headers.append("Authorization", "Bearer $token")
        val response = window.fetch(
            "$apiBaseUrl/api/publish/instagram",
            RequestInit(method = "POST", headers = headers, body = form)
        ).await()
        val text = response.text().await()
        PlatformPublishResult(
            success = response.ok,
            message = if (response.ok) "Instagram opublikowany: $text" else "Instagram nie opublikowany: $text"
        )
    }.getOrElse { exception ->
        PlatformPublishResult(false, "Blad publikacji Instagram: ${exception.message}")
    }
}

actual suspend fun scheduleScheduledPostOnServer(post: ScheduledPost): PlatformPublishResult {
    if (Platform.YOUTUBE !in post.platforms) {
        return PlatformPublishResult(true, "Brak platform wymagajacych serwerowego harmonogramu")
    }
    val token = currentAuthToken()
        ?: return PlatformPublishResult(false, "Zaloguj sie przed publikacja")
    val media = post.media.firstOrNull { it.isVideo } ?: post.media.firstOrNull()
        ?: return PlatformPublishResult(false, "YouTube wymaga pliku wideo")
    val file = MediaFileRegistry.file(media.uri) as? File
        ?: return PlatformPublishResult(false, "Plik jest niedostepny. Wybierz go ponownie.")
    if (!file.type.startsWith("video/")) {
        return PlatformPublishResult(false, "YouTube wymaga pliku wideo")
    }

    return runCatching {
        val form = FormData()
        val title = post.caption.lineSequence().firstOrNull { it.isNotBlank() }?.take(100) ?: "Tikthrow upload"
        form.append("title", title)
        form.append("description", post.caption)
        form.append("publicationType", post.youtubePublicationType())
        form.append("scheduledAtMillis", post.scheduledAtMillis().toString())
        form.append("media", file, file.name)
        val headers = Headers()
        headers.append("Authorization", "Bearer $token")
        val response = window.fetch(
            "$apiBaseUrl/api/publish/youtube/schedule",
            RequestInit(method = "POST", headers = headers, body = form)
        ).await()
        val text = response.text().await()
        PlatformPublishResult(response.ok, if (response.ok) "YouTube zaplanowany na serwerze: $text" else text)
    }.getOrElse { exception ->
        PlatformPublishResult(false, "Blad planowania YouTube: ${exception.message}")
    }
}

actual suspend fun scheduleTikTokPostOnServer(post: ScheduledPost): PlatformPublishResult {
    if (Platform.TIKTOK !in post.platforms) {
        return PlatformPublishResult(true, "Brak TikTok w platformach")
    }
    val token = currentAuthToken()
        ?: return PlatformPublishResult(false, "Zaloguj sie przed publikacja")
    val media = post.media.firstOrNull { it.isVideo } ?: post.media.firstOrNull()
        ?: return PlatformPublishResult(false, "TikTok wymaga pliku wideo")
    val file = MediaFileRegistry.file(media.uri) as? File
        ?: return PlatformPublishResult(false, "Plik jest niedostepny. Wybierz go ponownie.")
    if (!file.type.startsWith("video/")) {
        return PlatformPublishResult(false, "TikTok wymaga pliku wideo")
    }
    return runCatching {
        val form = FormData()
        form.append("caption", post.caption)
        // TikTok requires SELF_ONLY (private) for apps that haven't passed TikTok's app review
        // yet — PUBLIC_TO_EVERYONE gets rejected with 403 unaudited_client_can_only_post_to_private_accounts.
        form.append("privacyLevel", "SELF_ONLY")
        form.append("scheduledAtMillis", post.scheduledAtMillis().toString())
        form.append("media", file, file.name)
        val headers = Headers()
        headers.append("Authorization", "Bearer $token")
        val response = window.fetch(
            "$apiBaseUrl/api/publish/tiktok/schedule",
            RequestInit(method = "POST", headers = headers, body = form)
        ).await()
        val text = response.text().await()
        PlatformPublishResult(
            success = response.ok,
            message = if (response.ok) "TikTok zaplanowany na serwerze: $text" else text,
            serverId = if (response.ok) text.trim() else null,
        )
    }.getOrElse { exception ->
        PlatformPublishResult(false, "Blad planowania TikTok: ${exception.message}")
    }
}

// ── Queue & plan persistence on Web ─────────────────────────────────────────
// Mirrors the JVM implementation's wire contract exactly: the app's `/api/queue`
// and `/api/queue/plan` endpoints (bearer-authenticated). The nested
// `weeklySlots`/`platforms`/`accountIds` fields are real JSON arrays in the
// body (card #104), so they serialize/parse directly with no double-encoding.

private val planJson = Json { ignoreUnknownKeys = true }

private fun authHeaders(): Headers {
    val headers = Headers()
    currentAuthToken()?.let { headers.append("Authorization", "******") }
    return headers
}

/** Small helper to build a `Headers` with an Authorization (and optional Content-Type). */
private fun bearerHeaders(contentType: String? = null): Headers {
    val h = Headers()
    currentAuthToken()?.let { h.append("Authorization", "******") }
    contentType?.let { h.append("Content-Type", it) }
    return h
}

actual suspend fun savePlanToServer(plan: pl.zar.tikthrow.viewmodel.PublishingPlan): Boolean {
    val token = currentAuthToken() ?: return false
    return runCatching {
        // Card #104: the wire contract carries the lists as real JSON arrays,
        // so no intermediate string encoding is needed.
        val body = planJson.encodeToString(PlanBody(
            mode                = plan.mode.name,
            weeklySlots         = plan.weeklySlots,
            postsPerWeek        = plan.postsPerWeek,
            preferredHour       = plan.preferredHour,
            preferredMinute     = plan.preferredMinute,
            isActive            = plan.isActive,
            platforms           = plan.platforms.map { it.name },
            accountIds          = plan.accountIds.toList(),
            timezone            = plan.timezone,
            testIntervalMinutes = plan.testIntervalMinutes,
        ))
        val response = window.fetch(
            "$apiBaseUrl/api/queue/plan",
            RequestInit(
                method = "PUT",
                headers = bearerHeaders("application/json; charset=UTF-8"),
                body = body,
            )
        ).await()
        response.ok
    }.getOrDefault(false)
}

actual suspend fun loadPlanFromServer(): pl.zar.tikthrow.viewmodel.PublishingPlan? {
    val token = currentAuthToken() ?: return null
    return runCatching {
        val response = window.fetch("$apiBaseUrl/api/queue/plan", RequestInit(method = "GET", headers = authHeaders())).await()
        if (!response.ok) return@runCatching null
        val body = response.text().await()
        // Card #104: the server now returns the list fields as real JSON
        // arrays, so the DTO already holds them — no second parse.
        val dto = planJson.decodeFromString<PlanResponseDto>(body)
        val slots = dto.weeklySlots
        val platforms = dto.platforms
            .mapNotNull { name -> Platform.entries.firstOrNull { it.name == name } }
            .toSet()
        val accountIds = dto.accountIds.toSet()
        pl.zar.tikthrow.viewmodel.PublishingPlan(
            mode                = pl.zar.tikthrow.viewmodel.PlanMode.entries.firstOrNull { it.name == dto.mode } ?: pl.zar.tikthrow.viewmodel.PlanMode.SLOTS,
            weeklySlots         = slots,
            postsPerWeek        = dto.postsPerWeek,
            preferredHour       = dto.preferredHour,
            preferredMinute     = dto.preferredMinute,
            isActive            = dto.isActive,
            platforms           = platforms,
            accountIds          = accountIds,
            timezone            = dto.timezone,
            testIntervalMinutes = dto.testIntervalMinutes,
            lastPublishedAtMillis = dto.lastPublishedAt,
        )
    }.getOrNull()
}

actual suspend fun addPostToServerQueue(
    post: pl.zar.tikthrow.viewmodel.QueuedPost,
    media: List<pl.zar.tikthrow.ui.media.MediaItem>
): String? {
    val token = currentAuthToken() ?: return null
    return runCatching {
        val form = FormData()
        form.append("type", post.type.name)
        form.append("title", post.title)
        form.append("caption", post.caption)
        form.append("platforms", planJson.encodeToString(post.platforms.map { it.name }))
        post.youtubeAccountId?.let { form.append("youtubeAccountId", it) }
        post.tiktokAccountId?.let { form.append("tiktokAccountId", it) }

        // Attach the video as a multipart file part (only when a local File is available).
        val videoMedia = media.firstOrNull { it.isVideo }
        videoMedia?.let { m ->
            (MediaFileRegistry.file(m.uri) as? File)?.let { file ->
                form.append("media", file, m.fileName ?: file.name)
            }
        }

        val response = window.fetch(
            "$apiBaseUrl/api/queue",
            RequestInit(method = "POST", headers = authHeaders(), body = form)
        ).await()
        if (!response.ok) return@runCatching null
        response.text().await().trim().takeIf { it.isNotBlank() }
    }.getOrNull()
}

actual suspend fun updateQueuedPostOnServer(serverId: String, title: String, caption: String): Boolean {
    val token = currentAuthToken() ?: return false
    return runCatching {
        val body = planJson.encodeToString(UpdateQueuedPostBody(title, caption))
        val response = window.fetch(
            "$apiBaseUrl/api/queue/$serverId",
            RequestInit(
                method = "PUT",
                headers = bearerHeaders("application/json; charset=UTF-8"),
                body = body,
            )
        ).await()
        response.ok
    }.getOrDefault(false)
}

actual suspend fun removePostFromServerQueue(serverId: String): Boolean {
    val token = currentAuthToken() ?: return false
    return runCatching {
        val response = window.fetch(
            "$apiBaseUrl/api/queue/$serverId",
            RequestInit(method = "DELETE", headers = authHeaders())
        ).await()
        response.ok
    }.getOrDefault(false)
}

actual suspend fun loadServerQueue(): List<ServerQueueItem>? {
    val token = currentAuthToken() ?: return null
    return runCatching {
        val response = window.fetch("$apiBaseUrl/api/queue", RequestInit(method = "GET", headers = authHeaders())).await()
        if (!response.ok) return@runCatching null
        val body = response.text().await()
        planJson.decodeFromString<List<ServerQueueItemDto>>(body).map { dto ->
            ServerQueueItem(
                id               = dto.id,
                position         = dto.position,
                type             = dto.type,
                title            = dto.title,
                caption          = dto.caption,
                platforms        = runCatching { planJson.decodeFromString<List<String>>(dto.platforms) }.getOrDefault(emptyList()),
                youtubeAccountId = dto.youtubeAccountId.takeIf { it.isNotBlank() },
                tiktokAccountId  = dto.tiktokAccountId.takeIf { it.isNotBlank() },
                status           = dto.status,
                errorMessage     = dto.errorMessage,
            )
        }
    }.getOrNull()
}

actual suspend fun reorderServerQueue(serverIds: List<String>): Boolean {
    val token = currentAuthToken() ?: return false
    return runCatching {
        val body = planJson.encodeToString(ReorderBody(serverIds))
        val response = window.fetch(
            "$apiBaseUrl/api/queue/reorder",
            RequestInit(
                method = "PUT",
                headers = bearerHeaders("application/json; charset=UTF-8"),
                body = body,
            )
        ).await()
        response.ok
    }.getOrDefault(false)
}

actual suspend fun retryPostOnServer(serverId: String): String? {
    val token = currentAuthToken() ?: return "Zaloguj sie przed ponowieniem"
    return runCatching {
        val response = window.fetch(
            "$apiBaseUrl/api/queue/$serverId/retry",
            RequestInit(method = "POST", headers = authHeaders())
        ).await()
        if (response.ok) null
        else response.text().await().ifBlank { "HTTP ${response.status}" }
    }.getOrElse { it.message ?: "Nieznany błąd" }
}

@Serializable
private data class PlanBody(
    val mode: String,
    val weeklySlots: List<pl.zar.tikthrow.viewmodel.WeeklySlot>,
    val postsPerWeek: Int,
    val preferredHour: Int,
    val preferredMinute: Int,
    val isActive: Boolean,
    val platforms: List<String>,
    val accountIds: List<String>,
    val timezone: String,
    val testIntervalMinutes: Int?,
)

@Serializable
private data class PlanResponseDto(
    val mode: String = "SLOTS",
    val weeklySlots: List<pl.zar.tikthrow.viewmodel.WeeklySlot> = emptyList(),
    val postsPerWeek: Int = 3,
    val preferredHour: Int = 12,
    val preferredMinute: Int = 0,
    val isActive: Boolean = false,
    val platforms: List<String> = emptyList(),
    val accountIds: List<String> = emptyList(),
    val timezone: String = "UTC",
    val testIntervalMinutes: Int? = null,
    val lastPublishedAt: Long? = null,
)

@Serializable
private data class ServerQueueItemDto(
    val id: String,
    val position: Int,
    val type: String,
    val title: String,
    val caption: String,
    val platforms: String,
    val youtubeAccountId: String = "",
    val tiktokAccountId: String = "",
    val status: String,
    val errorMessage: String? = null,
)

@Serializable
private data class ReorderBody(val orderedIds: List<String>)

@Serializable
private data class UpdateQueuedPostBody(val title: String, val caption: String)

private fun ScheduledPost.scheduledAtMillis(): Long =
    scheduledLocalDateTimeAtMillis(scheduledDateMillis, scheduledHour, scheduledMinute)

private fun ScheduledPost.youtubePublicationType(): String =
    if (type == pl.zar.tikthrow.viewmodel.ContentType.SHORTS) "SHORTS" else "VIDEO"

private fun String.isVideoFileNameJs(): Boolean = when (substringAfterLast('.', "").lowercase()) {
    "mp4", "mov", "m4v", "webm", "mkv", "avi", "mpg", "mpeg", "3gp", "flv" -> true
    else -> false
}
